import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IAgoraRTCRemoteUser, ICameraVideoTrack } from 'agora-rtc-react';

import { useAppContext } from '@/app/AppContext';
import VideoDisplay, { VideoTrackInfo } from '@/app/components/VideoDisplay';
import WaitingParticipantTile from '@/app/components/WaitingParticipantTile';
import PictureInPictureTile from '@/app/components/PictureInPictureTile';
import { useMeetingData, useUserData } from '@/app/queries/MeetingQuery/context';

import { cn } from '@/lib/utils';
import { EVENTS, gtag } from '@/lib/gtag';

interface VideoTracksDisplayProps {
  localCameraTrack: ICameraVideoTrack;
  remoteUsers: IAgoraRTCRemoteUser[];
  showVideoTracks: boolean;
}

export default function VideoTracksDisplay({ localCameraTrack, remoteUsers, showVideoTracks }: VideoTracksDisplayProps) {
  const { camera, microphone } = useAppContext();

  const userData = useUserData();
  const { participants } = useMeetingData();

  const knownUids = useMemo(() => new Set(
    participants.flatMap(p => [p.user_id, p.screen_share_id])
  ), [participants]);

  const shareUids = useMemo(() => new Set(
    participants.map(p => p.screen_share_id)
  ), [participants]);

  const foreignShareUids = useMemo(() => new Set(
    participants.filter(p => p.user_id !== userData.uid).map(p => p.screen_share_id)
  ), [participants, userData.uid]);

  const filteredRemoteUsers = useMemo(
    () => remoteUsers.filter(user => knownUids.has(user.uid as number)),
    [remoteUsers, knownUids]
  );

  const [videoTracks, setVideoTracks] = useState<VideoTrackInfo[]>([]);
  const hasChosenLayout = useRef(false);
  // Whoever held the stage when a screen share took it, owed the stage back
  // once the share ends.
  const stagePredecessorUid = useRef<number | null>(null);
  // Mirrors videoTracks so initTracks can read the latest layout without
  // re-running on every tap — and without smuggling ref writes into a state
  // updater, which must stay pure.
  const latestTracks = useRef<VideoTrackInfo[]>([]);

  useEffect(() => {
    latestTracks.current = videoTracks;
  }, [videoTracks]);

  const handleVideoClick = useCallback((clickedVideoTrack: VideoTrackInfo) => {
    hasChosenLayout.current = true;
    gtag(EVENTS.FOCUS_STREAM);

    setVideoTracks(videoTracks => videoTracks.map(video => ({
      ...video,
      isEnlarged: video.source === clickedVideoTrack.source
    })));
  }, []);

  useEffect(function initTracks() {
    const prevTracks = latestTracks.current;
    const tracks: VideoTrackInfo[] = [
      {
        uid: userData.uid,
        source: localCameraTrack,
        sourceType: 'local',
        isEnlarged: prevTracks.find(track => track.uid === userData.uid)?.isEnlarged ?? true,
        hasAudio: microphone.isEnabled,
        hasVideo: camera.isEnabled,
      },
      ...filteredRemoteUsers.map(user => {
        const existingTrack = prevTracks.find(track => track.uid === user.uid);

        return {
          uid: user.uid as number,
          source: user,
          sourceType: 'remote' as VideoTrackInfo['sourceType'],
          isEnlarged: existingTrack ? existingTrack.isEnlarged : false,
          hasAudio: user.hasAudio,
          hasVideo: user.hasVideo,
        }
      }),
    ];

    const stageTo = (stageUid: number | null) =>
      tracks.map(track => ({ ...track, isEnlarged: track.uid === stageUid }));

    // A screen share is started to be watched: the moment another
    // participant's share appears, it takes the stage — no hunting for a
    // thumbnail to tap. Tapping any tile afterwards still swaps freely.
    const arrivedShare = tracks.find(track =>
      foreignShareUids.has(track.uid)
      && !prevTracks.some(prev => prev.uid === track.uid)
    );

    // The tile holding the stage can also leave mid-call — a share ending, a
    // reconnect. A vacant stage goes back to the share's predecessor, or
    // failing that to the other side's camera, or the local viewfinder —
    // never to a black panel.
    const stageIsVacant = prevTracks.length > 0 && !tracks.some(track => track.isEnlarged);

    // The host is on the call to SEE the customer: the moment the other
    // side's camera first appears, it takes the stage — unless the host has
    // already picked a layout by tapping. A screen share is not the other
    // side: the host's own share must not bump the customer off the stage.
    // The customer keeps their viewfinder.
    const isRemoteCamera = (track: VideoTrackInfo) =>
      track.sourceType === 'remote' && !shareUids.has(track.uid);
    const remoteCameraJustArrived =
      tracks.some(isRemoteCamera) && !prevTracks.some(isRemoteCamera);

    let nextTracks = tracks;
    if (arrivedShare) {
      stagePredecessorUid.current = prevTracks.find(track => track.isEnlarged)?.uid ?? null;
      nextTracks = stageTo(arrivedShare.uid);
    } else if (stageIsVacant) {
      const remoteCameraUid = tracks.find(isRemoteCamera)?.uid;
      const predecessorUid = stagePredecessorUid.current;
      nextTracks = stageTo(
        tracks.some(track => track.uid === predecessorUid)
          ? predecessorUid
          : remoteCameraUid ?? userData.uid
      );
      stagePredecessorUid.current = null;
    } else if (userData.isHost && !hasChosenLayout.current && remoteCameraJustArrived) {
      nextTracks = stageTo(tracks.find(isRemoteCamera)!.uid);
    }

    latestTracks.current = nextTracks;
    setVideoTracks(nextTracks);
  }, [localCameraTrack, filteredRemoteUsers, userData.uid, userData.isHost, microphone.isEnabled, camera.isEnabled, shareUids, foreignShareUids]);

  const stageTracks = videoTracks.filter(video => video.isEnlarged);
  const pictureInPictureTracks = videoTracks.filter(video => !video.isEnlarged);

  const nameOf = useCallback(
    (uid: number) => participants.find(participant => participant.user_id === uid)?.user_name,
    [participants]
  );

  const expectedGuest = useMemo(
    () => participants.find(participant => participant.user_id !== userData.uid),
    [participants, userData.uid]
  );
  const awaitedGuestName = filteredRemoteUsers.length === 0 ? expectedGuest?.user_name : undefined;

  // On a phone the video is the whole screen and everything else floats on top
  // of it. A desktop has room to lay the call out properly instead: the stage
  // becomes a panel and the other participants stand beside it in a column.
  return (
    <div
      className={cn(
        'absolute inset-0 md:relative md:inset-auto md:flex md:min-h-0 md:flex-1 md:gap-4 md:px-4',
        { 'invisible': !showVideoTracks },
      )}>
      <div className="absolute inset-0 md:relative md:inset-auto md:min-w-0 md:flex-1 md:overflow-hidden md:rounded-2xl md:bg-black">
        {stageTracks.map(video => (
          <Fragment key={video.uid}>
            <VideoDisplay
              className={cn('MainVideo absolute inset-0', { 'is-disabled': !video.source })}
              onClick={handleVideoClick} variant="stage" videoTrack={video} />
          </Fragment>
        ))}
      </div>

      {(pictureInPictureTracks.length > 0 || awaitedGuestName) && (
        <div className="absolute right-4 top-safe-top z-20 flex flex-col gap-3 md:static md:w-64 md:shrink-0">
          {awaitedGuestName && <WaitingParticipantTile name={awaitedGuestName} />}
          {pictureInPictureTracks.map(video => (
            <PictureInPictureTile
              key={video.uid}
              isScreenShare={shareUids.has(video.uid)}
              name={nameOf(video.uid)}
              onSwap={handleVideoClick}
              videoTrack={video}
            />
          ))}
        </div>
      )}
    </div>
  );
}
